import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../../services/apiService';
import {
  CreditCard, Key, BarChart3, Zap, Database, ChevronRight, Boxes, Activity,
} from 'lucide-react';

interface DashboardData {
  creditBalance: number;
  totalCreditsUsed: number;
  apiKeysCount: number;
  activeKeysCount: number;
}

interface TDUsageData {
  plan: {
    displayName: string;
    freeTaskDetQuota: number;
  };
  freeQuotaRemaining: number;
  currentPeriodCount: number;
  totalLifetime: number;
  nextResetDate: string | null;
  isAdmin: boolean;
}

// ─── Widget card ──────────────────────────────────────────────────────────────

interface WidgetCardProps {
  icon: React.ReactNode;
  iconBg: string;
  title: string;
  value: React.ReactNode;
  subtitle?: string;
  to: string;
  progress?: { used: number; total: number };
}

function WidgetCard({ icon, iconBg, title, value, subtitle, to, progress }: WidgetCardProps) {
  const navigate = useNavigate();
  const hasProgress = progress && progress.total > 0;
  const pct = hasProgress ? Math.min(100, Math.round((progress!.used / progress!.total) * 100)) : 0;

  return (
    <div
      onClick={() => navigate(to)}
      className="bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md hover:border-gray-200 transition-all cursor-pointer p-5 flex flex-col gap-3"
    >
      <div className="flex items-start justify-between">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${iconBg}`}>
          {icon}
        </div>
        <ChevronRight size={16} className="text-gray-300 mt-1" />
      </div>

      <div>
        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{title}</p>
        <p className="text-2xl font-bold text-gray-900 mt-0.5 leading-tight">{value}</p>
        {subtitle && <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p>}
      </div>

      {hasProgress && (
        <div className="space-y-1">
          <div className="w-full bg-gray-100 rounded-full h-1.5">
            <div
              className={`h-1.5 rounded-full transition-all ${pct >= 90 ? 'bg-red-400' : pct >= 70 ? 'bg-amber-400' : 'bg-blue-400'}`}
              style={{ width: `${pct}%` }}
            />
          </div>
          <p className="text-xs text-gray-400">{pct}% used</p>
        </div>
      )}
    </div>
  );
}

// ─── Skeleton card ────────────────────────────────────────────────────────────

function SkeletonCard() {
  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 flex flex-col gap-3 animate-pulse">
      <div className="flex items-start justify-between">
        <div className="w-10 h-10 rounded-lg bg-gray-100" />
        <div className="w-4 h-4 rounded bg-gray-100 mt-1" />
      </div>
      <div className="space-y-2">
        <div className="h-3 w-20 bg-gray-100 rounded" />
        <div className="h-7 w-28 bg-gray-100 rounded" />
        <div className="h-3 w-24 bg-gray-100 rounded" />
      </div>
    </div>
  );
}

// ─── Dashboard ────────────────────────────────────────────────────────────────

export default function Dashboard() {
  const [data, setData] = useState<DashboardData | null>(null);
  const [tdUsage, setTdUsage] = useState<TDUsageData | null>(null);
  const [customModelCount, setCustomModelCount] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const subscriptionsEnabled = process.env.REACT_APP_ENABLE_SUBSCRIPTIONS !== 'false';

  useEffect(() => {
    const load = async () => {
      try {
        const [dashboardData, tdData, modelsData] = await Promise.all([
          apiService.getDashboard(),
          subscriptionsEnabled ? apiService.getTaskDetUsage().catch(() => null) : Promise.resolve(null),
          apiService.get('/portal/models/custom').then(r => {
            const arr = Array.isArray(r) ? r : (r?.data ?? []);
            return arr.length;
          }).catch(() => null),
        ]);
        setData(dashboardData);
        setTdUsage(tdData);
        setCustomModelCount(modelsData);
      } catch (err: any) {
        setError(err.message || 'Failed to load dashboard');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg">
        Error: {error}
      </div>
    );
  }

  const freeQuotaTotal = tdUsage?.plan?.freeTaskDetQuota ?? 0;
  const freeQuotaUsed = freeQuotaTotal - (tdUsage?.freeQuotaRemaining ?? 0);

  const formatDate = (iso: string | null) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  };

  const planLabel = tdUsage?.isAdmin
    ? 'Unlimited'
    : (tdUsage?.plan?.displayName ?? '—');

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="mt-1 text-sm text-gray-500">Your portal at a glance</p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        {loading ? (
          Array.from({ length: subscriptionsEnabled ? 8 : 3 }).map((_, i) => <SkeletonCard key={i} />)
        ) : (
          <>
            {subscriptionsEnabled && (
              <>
                {/* 1 — Current Plan */}
                <WidgetCard
                  icon={<Zap size={20} className="text-purple-600" />}
                  iconBg="bg-purple-50"
                  title="Current Plan"
                  value={
                    tdUsage?.isAdmin
                      ? <span className="flex items-center gap-1.5">Unlimited <span className="text-xs font-medium bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded">Admin</span></span>
                      : planLabel
                  }
                  subtitle={tdUsage?.nextResetDate ? `Resets ${formatDate(tdUsage.nextResetDate)}` : 'Manage subscription'}
                  to="/plans"
                />

                {/* 2 — TD Free Quota */}
                <WidgetCard
                  icon={<Database size={20} className="text-blue-600" />}
                  iconBg="bg-blue-50"
                  title="Task Det. Free Quota"
                  value={tdUsage ? `${(tdUsage.freeQuotaRemaining ?? 0).toLocaleString()} left` : '—'}
                  subtitle={freeQuotaTotal > 0 ? `of ${freeQuotaTotal.toLocaleString()} this period` : 'This billing period'}
                  to="/plans"
                  progress={freeQuotaTotal > 0 ? { used: freeQuotaUsed, total: freeQuotaTotal } : undefined}
                />

                {/* 3 — TDs Used This Period */}
                <WidgetCard
                  icon={<BarChart3 size={20} className="text-amber-600" />}
                  iconBg="bg-amber-50"
                  title="Task Dets. Used"
                  value={tdUsage ? (tdUsage.currentPeriodCount ?? 0).toLocaleString() : '—'}
                  subtitle="This billing period"
                  to="/plans"
                />

                {/* 4 — Lifetime TDs */}
                <WidgetCard
                  icon={<Activity size={20} className="text-indigo-600" />}
                  iconBg="bg-indigo-50"
                  title="Lifetime Task Dets."
                  value={tdUsage ? (tdUsage.totalLifetime ?? 0).toLocaleString() : '—'}
                  subtitle="All-time total"
                  to="/plans"
                />

                {/* 5 — Credit Balance */}
                <WidgetCard
                  icon={<CreditCard size={20} className="text-green-600" />}
                  iconBg="bg-green-50"
                  title="Credit Balance"
                  value={`$${(data?.creditBalance ?? 0).toFixed(4)}`}
                  subtitle="Available to spend"
                  to="/plans"
                />
              </>
            )}

            {/* 6 — Usage Analytics */}
            <WidgetCard
              icon={<BarChart3 size={20} className="text-gray-500" />}
              iconBg="bg-gray-50"
              title="Usage Analytics"
              value={`$${(data?.totalCreditsUsed ?? 0).toFixed(4)}`}
              subtitle="Spent lifetime"
              to="/usage"
            />

            {/* 7 — API Keys */}
            <WidgetCard
              icon={<Key size={20} className="text-slate-600" />}
              iconBg="bg-slate-50"
              title="API Keys"
              value={data ? `${data.activeKeysCount} active` : '—'}
              subtitle={data ? `${data.apiKeysCount} total` : 'Manage access'}
              to="/api-keys"
            />

            {/* 8 — Custom Models */}
            <WidgetCard
              icon={<Boxes size={20} className="text-orange-600" />}
              iconBg="bg-orange-50"
              title="Custom Models"
              value={customModelCount !== null ? customModelCount.toLocaleString() : '—'}
              subtitle="Configured endpoints"
              to="/models"
            />
          </>
        )}
      </div>
    </div>
  );
}
